import {
  Box, Card, CardContent, CardHeader, Divider, Stack, TextField,
  Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Pagination, MenuItem,
  Select, InputLabel, FormControl, TableSortLabel, Typography, Checkbox, Toolbar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import api from '@src/api/clients';
import ClientDropdown from '@src/components/ClientDropdown';
import { DateRangePicker } from '@src/components';
import { formatDateToYMD, getCurrentMonthDates } from '@src/utils/dateUtils';

const sanitizeFilters = (filters = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );

const normalizeNextUrl = (next) => {
  if (!next) return null;
  try {
    const parsed = new URL(next, window.location.origin);
    const relative = `${parsed.pathname}${parsed.search}`;
    let trimmed = relative.startsWith('/') ? relative.slice(1) : relative;
    if (trimmed.startsWith('api/')) trimmed = trimmed.slice(4);
    return trimmed;
  } catch (err) {
    return next.startsWith('/') ? next.slice(1) : next;
  }
};

const fetchAllFilteredBackendData = async ({ baseFilters = {}, pageSize = 500 } = {}) => {
  const filters = sanitizeFilters(baseFilters);
  const params = new URLSearchParams({ page: 1, page_size: pageSize });
  Object.entries(filters).forEach(([key, value]) => params.set(key, value));

  const visited = new Set();
  const rows = [];
  let nextUrl = `dashboard/backend-data/?${params.toString()}`;

  while (nextUrl && !visited.has(nextUrl)) {
    visited.add(nextUrl);
    // eslint-disable-next-line no-await-in-loop
    const res = await api.request({ url: nextUrl, method: 'GET' });
    const { data } = res || {};
    const { results = [], next } = data || {};
    results.forEach((row) => rows.push(row));
    const normalizedNext = normalizeNextUrl(next);
    nextUrl = normalizedNext && !visited.has(normalizedNext) ? normalizedNext : null;
  }

  return rows;
};

export default function BackendDataListPage() {
  const [defaultStart, defaultEnd] = getCurrentMonthDates();
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);

  const [filters, setFilters] = useState({
    phone: '',
    email: '',
    zip_code: '',
    client: '',
    issue: '',
    demo: '',
    has_sold: '',
    has_net_amount: '',
    status: '',
    territory: '',
    date_field: 'created_date',
    date_after: formatDateToYMD(defaultStart),
    date_before: formatDateToYMD(defaultEnd)
  });

  const pageSize = 100;

  const fetchBackendData = async (pageParam = page, activeFilters = filters) => {
    setLoading(true);
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(activeFilters)
          .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
          .filter(([_, v]) => v !== '')
      );
      const params = new URLSearchParams({
        page: pageParam,
        ordering: orderBy ? `${orderDirection === 'desc' ? '-' : ''}${orderBy}` : '',
        ...cleanedFilters,
      }).toString();

      const res = await api.request({ url: `dashboard/backend-data/?${params}`, method: 'GET' });
      setRecords(res.data.results);
      const count = res.data.count || 0;
      setTotalCount(count);
      setTotalPages(Math.ceil(count / pageSize));
    } catch (err) {
      console.error('Error fetching backend data:', err);
    } finally {
      setLoading(false);
    }
  };

  const isRowSelected = (id) => selectAllFiltered || selectedIds.includes(id);
  const currentPageIds = records.map((row) => row.unique_id || row.id);
  const allOnPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => isRowSelected(id));

  const handleToggleRow = (id) => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds([id]);
      return;
    }
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleToggleAllCurrent = () => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds([]);
      return;
    }

    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      const combined = new Set([...selectedIds, ...currentPageIds]);
      setSelectedIds(Array.from(combined));
    }
  };

  const handleSelectAllFiltered = () => {
    setSelectAllFiltered(true);
    setSelectedIds([]);
  };

  const handleClearSelection = () => {
    setSelectAllFiltered(false);
    setSelectedIds([]);
  };

  const formatDateForExport = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const exportBackendData = async () => {
    try {
      const dateFields = new Set(['created_date', 'appointment_date', 'sold_date', 'updated_date', 'refresh_date', 'updated_at']);
      const baseFilters = sanitizeFilters(filters);
      baseFilters.page_size = 500;

      let exportRows = [];
      if (selectAllFiltered) {
        exportRows = await fetchAllFilteredBackendData({ baseFilters });
      } else if (selectedIds.length) {
        const currentMap = new Map(records.map((row) => [row.unique_id || row.id, row]));
        const missing = selectedIds.filter((id) => !currentMap.has(id));
        if (missing.length === 0) {
          exportRows = selectedIds.map((id) => currentMap.get(id)).filter(Boolean);
        } else {
          const allFiltered = await fetchAllFilteredBackendData({ baseFilters });
          exportRows = allFiltered.filter((row) => selectedIds.includes(row.unique_id || row.id));
        }
      } else {
        return;
      }

      if (!exportRows.length) return;

      const headerSet = new Set();
      exportRows.forEach((row) => Object.keys(row || {}).forEach((key) => headerSet.add(key)));
      const headers = Array.from(headerSet).filter((field) =>
        exportRows.some((row) => {
          const value = row ? row[field] : undefined;
          return value !== null && value !== undefined && value !== '';
        })
      );
      if (!headers.length) return;

      const csvRows = [
        headers.join(','),
        ...exportRows.map((row) =>
          headers
            .map((key) => {
              let value = row ? row[key] : '';
              if (value === null || value === undefined) return '';
              if (dateFields.has(key)) value = formatDateForExport(value);
              if (typeof value === 'object') {
                return JSON.stringify(value).replace(/"/g, '""');
              }
              return String(value).replace(/"/g, '""');
            })
            .map((cell) => `"${cell}"`)
            .join(',')
        ),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'backend-data-export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting backend data:', err);
    }
  };

  useEffect(() => {
    fetchBackendData();
  }, [page, orderBy, orderDirection]);

  const handleSearch = () => {
    setPage(1);
    setSelectAllFiltered(false);
    setSelectedIds([]);
    fetchBackendData(1);
  };

  const handleInputChange = (field, value) => {
    setFilters(prev => {
      const next = { ...prev, [field]: value };
      setPage(1);
      setSelectAllFiltered(false);
      setSelectedIds([]);
      fetchBackendData(1, next);
      return next;
    });
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrderDirection('asc');
    }
  };

  const toEST = (utc) =>
    utc ? new Date(utc).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '-';

  const renderBooleanDropdown = (label, field) => (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={filters[field]}
        onChange={(e) => handleInputChange(field, e.target.value)}
        label={label}
      >
        <MenuItem value=""><em>Any</em></MenuItem>
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </Select>
    </FormControl>
  );

  const renderSortableHeader = (label, field) => (
    <TableCell sortDirection={orderBy === field ? orderDirection : false}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? orderDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const searchFieldBaseSx = { '& .MuiInputBase-root': { height: 40 } };
  const selectFieldBaseSx = {
    '& .MuiInputBase-root': { height: 40, display: 'flex', alignItems: 'center' },
    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 },
  };

  const handleDateRangeChange = ({ selection }) => {
    const { startDate: nextStart, endDate: nextEnd } = selection;
    setStartDate(nextStart);
    setEndDate(nextEnd);

    const formattedStart = nextStart ? formatDateToYMD(nextStart) : '';
    const formattedEnd = nextEnd ? formatDateToYMD(nextEnd) : '';

    setFilters((prev) => {
      const nextFilters = { ...prev, date_after: formattedStart, date_before: formattedEnd };
      if (page === 1) {
        fetchBackendData(1, nextFilters);
      }
      return nextFilters;
    });

    if (page !== 1) {
      setPage(1);
    }
    setSelectAllFiltered(false);
    setSelectedIds([]);
  };

  return (
    <Box p={3}>
      <Card>
        <CardHeader title="Backend Data Records" />
        <Divider />
        <CardContent>
          <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2.5,
              mb: 2,
              bgcolor: theme.palette.background.level1 ?? theme.palette.background.default,
              boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
            })}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Filters & search
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Narrow results by client, dates, and status.
                </Typography>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Date & status filters
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  alignItems="flex-start"
                >
                  <Box
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      p: 1.25,
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      minWidth: { xs: '100%', sm: 320 },
                      maxWidth: { sm: 420 },
                      bgcolor: theme.palette.background.paper,
                    })}
                  >
                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: '100%', sm: 200 }, ...selectFieldBaseSx }}
                    >
                      <InputLabel>Date field</InputLabel>
                      <Select
                        value={filters.date_field}
                        onChange={(e) => handleInputChange('date_field', e.target.value)}
                        label="Date field"
                      >
                        <MenuItem value="created_date">Created date</MenuItem>
                        <MenuItem value="appointment_date">Appointment date</MenuItem>
                        <MenuItem value="sold_date">Sold date</MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ minWidth: { xs: '100%', sm: 260 }, width: '100%' }}>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          Date range
                        </Typography>
                        <DateRangePicker
                          startDate={startDate}
                          endDate={endDate}
                          onChange={handleDateRangeChange}
                          style={{ width: '100%' }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    alignItems="center"
                    sx={{ flex: 1 }}
                  >
                    <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                      <ClientDropdown value={filters.client} onChange={(value) => handleInputChange('client', value)} />
                    </Box>
                    {renderBooleanDropdown('Gross Sold', 'has_sold')}
                    {renderBooleanDropdown('Net $', 'has_net_amount')}
                    {renderBooleanDropdown('Issue', 'issue')}
                    {renderBooleanDropdown('Demo', 'demo')}
                  </Stack>
                </Stack>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Search fields
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
                <TextField
                  label="Phone"
                  value={filters.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 180 }, ...searchFieldBaseSx }}
                />
                <TextField
                  label="Email"
                  value={filters.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 200 }, ...searchFieldBaseSx }}
                />
                <TextField
                  label="Zip Code"
                  value={filters.zip_code}
                  onChange={(e) => handleInputChange('zip_code', e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 140 }, ...searchFieldBaseSx }}
                />
                <TextField
                  label="Territory"
                  value={filters.territory}
                  onChange={(e) => handleInputChange('territory', e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 140 }, ...searchFieldBaseSx }}
                />
                <TextField
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 140 }, ...searchFieldBaseSx, display: 'none' }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} sx={{ height: 40, minWidth: 96 }}>
                    Search
                  </Button>
                </Box>
              </Stack>
              </Stack>
            </Stack>
          </Box>

          <Toolbar
            sx={{
              px: 0,
              mb: 1,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {selectAllFiltered ? `All ${totalCount || 0} filtered records selected` : `${selectedIds.length} selected`}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="text"
                size="small"
                onClick={handleSelectAllFiltered}
                disabled={selectAllFiltered || totalCount === 0}
              >
                Select all filtered
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={handleClearSelection}
                disabled={!selectAllFiltered && selectedIds.length === 0}
              >
                Clear selection
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={exportBackendData}
                disabled={!selectAllFiltered && selectedIds.length === 0}
              >
                Export selected
              </Button>
            </Stack>
          </Toolbar>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={!selectAllFiltered && selectedIds.length > 0 && !allOnPageSelected}
                      checked={selectAllFiltered || allOnPageSelected}
                      onChange={handleToggleAllCurrent}
                      inputProps={{ 'aria-label': 'Select all records on page' }}
                    />
                  </TableCell>
                  {renderSortableHeader('Client', 'client__name')}
                  {renderSortableHeader('Refresh Date', 'updated_date')}
                  {renderSortableHeader('Created Date', 'created_date')}
                  {renderSortableHeader('Appointment Date', 'appointment_date')}
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell>Territory</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Demo</TableCell>
                  {renderSortableHeader('Sold Date', 'sold_date')}
                  {renderSortableHeader('Gross Amount', 'gross_amount')}
                  {renderSortableHeader('Net Amount', 'net_amount')}
                  <TableCell>First Lead Name</TableCell>
                  <TableCell>Lead Campaign</TableCell>
                  <TableCell>Lead Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => {
                  const lead = record.related_leads?.[0];
                  const rowId = record.unique_id || record.id;
                  return (
                    <TableRow key={rowId} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isRowSelected(rowId)}
                          onChange={() => handleToggleRow(rowId)}
                          inputProps={{ 'aria-label': `Select record ${rowId}` }}
                        />
                      </TableCell>
                      <TableCell>{record.client_name || '-'}</TableCell>
                      <TableCell>{toEST(record.updated_date)}</TableCell>
                      <TableCell>{record.created_date}</TableCell>
                      <TableCell>{record.appointment_date}</TableCell>
                      <TableCell>{record.phone || '-'}</TableCell>
                      <TableCell>{record.email || '-'}</TableCell>
                      <TableCell>{record.zip_code || '-'}</TableCell>
                      <TableCell>{record.territory || '-'}</TableCell>
                      <TableCell>{record.status || '-'}</TableCell>
                      <TableCell>{record.issue ? '✅' : '❌'}</TableCell>
                      <TableCell>{record.demo ? '✅' : '❌'}</TableCell>
                      <TableCell>{record.sold_date}</TableCell>
                      <TableCell>{record.gross_amount ? `$${record.gross_amount}` : '-'}</TableCell>
                      <TableCell>{record.net_amount ? `$${record.net_amount}` : '-'}</TableCell>
                      <TableCell>{lead ? `${lead.first_name || ''} ${lead.last_name || ''}` : '-'}</TableCell>
                      <TableCell>{lead?.campaign_name || '-'}</TableCell>
                      <TableCell>{lead?.source || '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={17} align="center">
                      {loading ? 'Loading...' : 'No backend records found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
